/**
 * Reference Fact 字段别名表 + 自动匹配工具
 *
 * 用于灵活导入时，将用户 Excel 列名自动对应到标准字段。
 * 不做拼音相似度、不做 AI 识别、不做复杂模糊匹配。
 */

// ==================== 采购应付汇总标准字段定义 ====================

export const PURCHASE_FIELDS = [
  { key: '年月', label: '年月', required: true },
  { key: '供应商名称', label: '供应商名称', required: true },
  { key: '供应商ID', label: '供应商ID', required: false },
  { key: '期初余额', label: '期初余额', required: false },
  { key: '采购入金额', label: '采购入金额', required: false },
  { key: '采购入差异金额', label: '采购入差异金额', required: false },
  { key: '退款金额', label: '退款金额', required: false },
  { key: '支付金额', label: '支付金额', required: false },
  { key: 'TE5月处置扣除金额', label: 'TE5月处置扣除金额', required: false },
  { key: '期末余额', label: '期末余额', required: false },
  { key: '币种', label: '币种', required: false }
];

// ==================== 物流应付汇总标准字段定义 ====================

export const LOGISTICS_FIELDS = [
  { key: '年月', label: '年月', required: true },
  { key: '物流商名称', label: '物流商名称', required: true },
  { key: '物流商ID', label: '物流商ID', required: false },
  { key: '期初金额', label: '期初金额', required: false },
  { key: '头程金额', label: '头程金额', required: false },
  { key: '尾程金额', label: '尾程金额', required: false },
  { key: '头程差异修正金额', label: '头程差异修正金额', required: false },
  { key: '赔偿金额', label: '赔偿金额', required: false },
  { key: '支付金额', label: '支付金额', required: false },
  { key: '期末金额', label: '期末金额', required: false },
  { key: '币种', label: '币种', required: false }
];

// ==================== 别名表 ====================

/**
 * 采购应付汇总字段别名映射。
 * key：标准字段名
 * value：同义别名集合（含标准名自身）
 */
const PURCHASE_ALIASES = {
  '年月': new Set(['年月', '期间', '月份', '会计期间', 'period', '会计月份', '业务月份']),
  '供应商名称': new Set(['供应商名称', '供应商名', '供货商名称', '供应商全称', 'supplier_name', 'supplierName', '供货商名']),
  '供应商ID': new Set(['供应商ID', '供应商编号', '供应商代码', '供货商ID', 'supplier_id', 'supplierId', '供应商编码']),
  '期初余额': new Set(['期初余额', '期初金额', '期初', 'opening_balance', 'openingBalance', '期初余额(元)', '月初余额']),
  '采购入金额': new Set(['采购入金额', '采购金额', '采购入金', 'purchase_amount', 'purchaseInAmount', '本期采购额', '采购发生额']),
  '采购入差异金额': new Set(['采购入差异金额', '采购差异', '采购差异金额', 'purchase_difference', 'purchaseDifferenceAmount', '采购差异额']),
  '退款金额': new Set(['退款金额', '退款', 'refund_amount', 'refundAmount', '退货金额', '采购退款']),
  '支付金额': new Set(['支付金额', '付款金额', '实付金额', '已付款', 'payment_amount', 'paymentAmount', '实际支付', '本期支付']),
  'TE5月处置扣除金额': new Set(['TE5月处置扣除金额', 'TE扣除', '处置扣除', 'te_deduction', 'deductionAmount', 'TE扣款', '5月处置扣除']),
  '期末余额': new Set(['期末余额', '期末金额', '期末', 'ending_balance', 'endingBalance', '期末余额(元)', '月末余额']),
  '币种': new Set(['币种', 'currency', '币别', '货币', '货币类型', '原币币种'])
};

/**
 * 物流应付汇总字段别名映射。
 */
const LOGISTICS_ALIASES = {
  '年月': new Set(['年月', '期间', '月份', '会计期间', 'period', '业务月份']),
  '物流商名称': new Set(['物流商名称', '物流公司', '物流商', '承运商', '承运商名称', 'logistics_provider', 'logisticsName', '物流供应商']),
  '物流商ID': new Set(['物流商ID', '物流商编号', '承运商ID', '物流商代码', 'logistics_id', 'logisticsProviderId']),
  '期初金额': new Set(['期初金额', '期初余额', '期初', 'opening_balance', 'openingBalance', '期初余额(元)']),
  '头程金额': new Set(['头程金额', '头程运费', '头程', 'head_freight', 'headFreightAmount', '头程费用']),
  '尾程金额': new Set(['尾程金额', '尾程运费', '尾程', 'tail_freight', 'tailFreightAmount', '尾程费用']),
  '头程差异修正金额': new Set(['头程差异修正金额', '头程差异', '头程差异修正', 'head_freight_adjustment', 'headFreightAdjustment', '头程修正']),
  '赔偿金额': new Set(['赔偿金额', '赔偿', '理赔金额', 'compensation', 'compensationAmount', '索赔金额']),
  '支付金额': new Set(['支付金额', '付款金额', '实付', 'payment_amount', 'paymentAmount', '实际支付', '物流付款']),
  '期末金额': new Set(['期末金额', '期末余额', '期末', 'ending_balance', 'endingBalance', '期末余额(元)']),
  '币种': new Set(['币种', 'currency', '币别', '货币'])
};

// ==================== 自动匹配 ====================

/**
 * 获取指定 factType 的标准字段列表。
 */
export function getStandardFields(factType) {
  if (factType === 'PURCHASE_PAYABLE_SUMMARY') return PURCHASE_FIELDS;
  if (factType === 'LOGISTICS_PAYABLE_SUMMARY') return LOGISTICS_FIELDS;
  return [];
}

/**
 * 获取指定 factType 的别名表。
 */
function getAliases(factType) {
  if (factType === 'PURCHASE_PAYABLE_SUMMARY') return PURCHASE_ALIASES;
  if (factType === 'LOGISTICS_PAYABLE_SUMMARY') return LOGISTICS_ALIASES;
  return {};
}

/**
 * 对单个 Excel 表头尝试自动匹配标准字段。
 *
 * @param {string} excelHeader  - Excel 实际列名
 * @param {string} factType     - 事实类型
 * @param {Set}    usedKeys     - 已被占用的标准字段 key
 * @returns {string|null} 匹配到的标准字段 key，无匹配返回 null
 */
export function autoMatchHeader(excelHeader, factType, usedKeys) {
  if (!excelHeader) return null;
  const trimmed = excelHeader.trim();

  const fields = getStandardFields(factType);
  const aliases = getAliases(factType);

  // 1. 精确匹配：Excel 列名等于标准字段名
  for (const field of fields) {
    if (usedKeys.has(field.key)) continue;
    if (trimmed === field.key) return field.key;
  }

  // 2. 别名匹配
  for (const field of fields) {
    if (usedKeys.has(field.key)) continue;
    const aliasSet = aliases[field.key];
    if (aliasSet && aliasSet.has(trimmed)) return field.key;
  }

  return null;
}

/**
 * 对给定的 Excel 表头列表，自动生成完整字段映射。
 *
 * @param {string[]} excelHeaders - Excel 表头列表
 * @param {string}   factType     - 事实类型
 * @returns {Array<{standardKey: string, excelHeader: string|null, matched: boolean}>}
 */
export function autoBuildMapping(excelHeaders, factType) {
  const fields = getStandardFields(factType);
  const usedKeys = new Set();

  // 先按顺序处理 Excel 表头，尝试匹配
  const headerToKey = {};
  for (const header of excelHeaders) {
    const key = autoMatchHeader(header, factType, usedKeys);
    if (key) {
      headerToKey[key] = header;
      usedKeys.add(key);
    }
  }

  // 构建映射结果
  return fields.map(field => {
    if (headerToKey[field.key]) {
      return { standardKey: field.key, excelHeader: headerToKey[field.key], matched: true };
    }
    return { standardKey: field.key, excelHeader: null, matched: false };
  });
}

/**
 * 根据字段映射，将原始 Excel rows 转换为标准 key 的 rows。
 *
 * @param {Array<{standardKey: string, excelHeader: string|null}>} mapping
 * @param {Array<Object>} rawRows - 原始 rows（key 为 Excel 列名）
 * @returns {Array<Object>} 转换后的 rows（key 为标准字段名）
 */
export function transformRowsByMapping(mapping, rawRows) {
  return rawRows.map(row => {
    const result = {};
    for (const item of mapping) {
      if (item.excelHeader && item.excelHeader in row) {
        result[item.standardKey] = row[item.excelHeader];
      }
    }
    return result;
  });
}

/**
 * 找出 Excel 中未被映射的列名。
 *
 * @param {string[]} excelHeaders
 * @param {Array<{standardKey: string, excelHeader: string|null}>} mapping
 * @returns {string[]}
 */
export function findUnmappedExcelHeaders(excelHeaders, mapping) {
  const usedHeaders = new Set(
    mapping.filter(m => m.excelHeader).map(m => m.excelHeader)
  );
  return excelHeaders.filter(h => !usedHeaders.has(h));
}
